import { useCallback, useEffect, useState } from "react";

const API_URL = process.env.REACT_APP_API_URL;

const getToken = () => localStorage.getItem("token");

const authHeaders = (withJson = false) => ({
  Authorization: `Bearer ${getToken()}`,
  ...(withJson ? { "Content-Type": "application/json" } : {}),
});

/* -------------------------------------------------------------------------- */
/*                                Raw API calls                               */
/* -------------------------------------------------------------------------- */

const moderationRequest = async (reviewId, action, body = {}) => {
  const response = await fetch(
    `${API_URL}api/admin/reviews/${reviewId}/${action}`,
    {
      method: "PUT",
      headers: authHeaders(true),
      body: JSON.stringify(body),
    }
  );

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(
      data?.message || data?.errorMessage || `Failed to ${action} review.`
    );
  }

  return data;
};

export const approveReviewApi = (reviewId, note = "") =>
  moderationRequest(reviewId, "approve", { note });

export const removeReviewApi = (reviewId, reason, note = "") =>
  moderationRequest(reviewId, "remove", { reason, note });

export const restoreReviewApi = (reviewId, note = "") =>
  moderationRequest(reviewId, "restore", { note });

export const getReviewRemovalReasonsApi = async () => {
  const response = await fetch(`${API_URL}api/admin/reviews/removal-reasons`, {
    headers: authHeaders(),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(
      data?.message ||
        data?.errorMessage ||
        "Failed to fetch removal reasons."
    );
  }

  return data;
};

export const normalizeRemovalReasons = (response) => {
  const payload = response?.data ?? response;

  const reasons = Array.isArray(payload)
    ? payload
    : payload?.reasons ??
      payload?.removal_reasons ??
      payload?.items ??
      payload?.data?.reasons ??
      payload?.data?.removal_reasons ??
      payload?.data?.items ??
      [];

  if (!Array.isArray(reasons)) return [];

  return reasons
    .map((item) => {
      if (typeof item === "string") {
        return { value: item, label: item };
      }

      if (!item || typeof item !== "object") return null;

      const value = item.value ?? item.code ?? item.key ?? item.id ?? item.reason;
      const label =
        item.label ??
        item.name ??
        item.title ??
        item.description ??
        item.reason ??
        item.value;

      if (value == null || label == null) return null;

      return { value: String(value), label: String(label) };
    })
    .filter(Boolean);
};

/* -------------------------------------------------------------------------- */
/*                                    Hooks                                   */
/* -------------------------------------------------------------------------- */

/** Garage dropdown options. */
export const useGarageOptions = () => {
  const [garages, setGarages] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const fetchGarages = async () => {
      try {
        const params = new URLSearchParams({
          page: "0",
          limit: "100",
          search: "",
        });

        const response = await fetch(`${API_URL}api/admin/garages?${params}`, {
          headers: authHeaders(),
        });

        const json = await response.json();

        if (!cancelled && response.ok) {
          setGarages(json.data?.garages || []);
        }
      } catch (error) {
        console.error("Error fetching garages:", error);
      }
    };

    fetchGarages();

    return () => {
      cancelled = true;
    };
  }, []);

  return garages;
};

/** Reviewer (user) dropdown options. */
export const useReviewerOptions = () => {
  const [users, setUsers] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const fetchUsers = async () => {
      try {
        const params = new URLSearchParams({
          page: "0",
          role: "user",
          limit: "100",
          search: "",
        });

        const response = await fetch(`${API_URL}api/admin/users?${params}`, {
          headers: authHeaders(),
        });

        const json = await response.json();

        if (!cancelled && response.ok) {
          setUsers(json.data?.users || []);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    fetchUsers();

    return () => {
      cancelled = true;
    };
  }, []);

  return users;
};

/** Paginated + filtered reviews list. */
export const useReviews = ({
  page,
  limit,
  query,
  status,
  garageId,
  userId,
  rating,
  onError,
}) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  });
  const [counts, setCounts] = useState({
    visible: 0,
    flagged: 0,
    removed: 0,
    total: 0,
  });

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;

    const fetchAllReviews = async () => {
      try {
        setLoading(true);

        const params = new URLSearchParams({
          page: String(page),
          limit: String(limit),
          search: query,
        });

        if (status) params.set("status", status);
        if (garageId) params.set("garage_id", garageId);
        if (userId) params.set("user_id", userId);
        if (rating) params.set("rating", rating);

        const response = await fetch(`${API_URL}api/admin/reviews?${params}`, {
          headers: authHeaders(),
        });

        const json = await response.json();

        if (cancelled) return;

        if (!response.ok) {
          setRecords([]);
          setPagination({
            total: 0,
            totalPages: 0,
            hasNext: false,
            hasPrev: false,
          });

          onError?.(json?.message || "Failed to fetch reviews.");
          return;
        }

        const data = json.data || {};
        const reviews = data.reviews || [];
        const pg = data.pagination || {};

        setRecords(
          [...reviews].sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at)
          )
        );

        const totalCount = pg.total_count ?? data.total ?? 0;

        setPagination({
          total: totalCount,
          totalPages: pg.total_pages ?? Math.ceil(totalCount / limit),
          hasNext: pg.has_next ?? data.hasNext ?? false,
          hasPrev: pg.has_prev ?? data.hasPrev ?? false,
        });

        setCounts({
          visible: data.counts?.visible ?? 0,
          flagged: data.counts?.flagged ?? 0,
          removed: data.counts?.removed ?? 0,
          total: data.counts?.total ?? totalCount,
        });
      } catch (error) {
        console.error("Error fetching reviews:", error);

        if (!cancelled) {
          onError?.("Something went wrong while fetching reviews.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAllReviews();

    return () => {
      cancelled = true;
    };
    // onError intentionally excluded so an inline callback doesn't refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, query, status, garageId, userId, rating, refreshKey]);

  return { records, loading, counts, pagination, refresh };
};

/** Approve / remove / restore actions + removal reasons. */
export const useReviewActions = () => {
  const [actionLoadingId, setActionLoadingId] = useState(null);

  const run = useCallback(async (reviewId, fn) => {
    try {
      setActionLoadingId(reviewId);
      return await fn();
    } finally {
      setActionLoadingId(null);
    }
  }, []);

  const approveReview = useCallback(
    (reviewId, note = "") =>
      run(reviewId, () => approveReviewApi(reviewId, note)),
    [run]
  );

  const removeReview = useCallback(
    (reviewId, reason, note = "") =>
      run(reviewId, () => removeReviewApi(reviewId, reason, note)),
    [run]
  );

  const restoreReview = useCallback(
    (reviewId, note = "") =>
      run(reviewId, () => restoreReviewApi(reviewId, note)),
    [run]
  );

  const fetchRemovalReasons = useCallback(
    (reviewId) =>
      run(reviewId, async () =>
        normalizeRemovalReasons(await getReviewRemovalReasonsApi())
      ),
    [run]
  );

  return {
    actionLoadingId,
    approveReview,
    removeReview,
    restoreReview,
    fetchRemovalReasons,
  };
};

/** Combined hook: everything the reviews page needs. */
const useReviewGarage = (filters) => {
  const garages = useGarageOptions();
  const users = useReviewerOptions();
  const reviews = useReviews(filters);
  const actions = useReviewActions();

  return { garages, users, ...reviews, ...actions };
};

export default useReviewGarage;